import { cn } from '@/lib/utils'
import MarkdownRenderer from '@/modules/admin/wizard-forms-builder/unified/ui-components/MarkdownRenderer'
import DOMPurify from 'dompurify'
import { ArrowDown, FileText, Folder, RotateCcw, Share2, Star, Trash2, X } from 'lucide-react'
import { useMemo } from 'react'
import { FinderItem } from '../types'
import { formatBytes, formatDate, getItemIcon, getOwnerName } from '../utils'

interface FinderPreviewProps {
    item: FinderItem
    onClose: () => void
    onOpen: () => void
    onDownload: () => void
    onShare: () => void
    onDelete: () => void
    onFavoriteToggle: () => void
    onNavigateToItem?: (item: FinderItem) => void
    isTrashSection?: boolean
    onRestore?: () => void
    onPermanentDelete?: () => void
}

export function FinderPreview({
    item,
    onClose,
    onOpen,
    onDownload,
    onShare,
    onDelete,
    onFavoriteToggle,
    onNavigateToItem,
    isTrashSection = false,
    onRestore,
    onPermanentDelete,
}: FinderPreviewProps) {
    const icon = getItemIcon(item)

    const contentPreview = useMemo(() => {
        // For folders
        if (item.type === 'folder') {
            const children = item.items || []
            const itemCount = item.itemCount ?? children.length

            // If we have loaded children, show them
            if (children.length > 0) {
                return (
                    <div className='space-y-1'>
                        <p className='text-[10px] text-gray-400 mb-2'>
                            {children.length} item{children.length !== 1 ? 's' : ''}
                        </p>
                        <div className='space-y-0.5 max-h-32 overflow-y-auto'>
                            {children.slice(0, 10).map((child: any) => (
                                <div
                                    key={child.id}
                                    className='flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors'
                                    onClick={() => onNavigateToItem?.(child)}
                                >
                                    {child.isFolder ? (
                                        <Folder className='w-3 h-3 text-amber-500 shrink-0' />
                                    ) : (
                                        <FileText className='w-3 h-3 text-blue-500 shrink-0' />
                                    )}
                                    <span className='text-xs text-gray-700 truncate'>{child.name}</span>
                                </div>
                            ))}
                            {children.length > 10 && (
                                <p className='text-[10px] text-gray-400 px-2 py-1'>+{children.length - 10} more...</p>
                            )}
                        </div>
                    </div>
                )
            }

            // If we have a count but no loaded children, show the count with open prompt
            if (itemCount > 0) {
                return (
                    <div className='text-center py-4'>
                        <Folder className='w-6 h-6 text-amber-400 mx-auto mb-2' />
                        <p className='text-xs text-gray-600'>
                            {itemCount} item{itemCount !== 1 ? 's' : ''}
                        </p>
                        <button onClick={onOpen} className='text-xs text-[#63316B] hover:underline mt-1'>
                            Open to view contents
                        </button>
                    </div>
                )
            }

            // Empty folder
            return (
                <div className='text-center py-4'>
                    <Folder className='w-6 h-6 text-gray-300 mx-auto mb-2' />
                    <p className='text-xs text-gray-400'>This folder is empty</p>
                </div>
            )
        }

        // For images
        if (item.data_type === 'image' && item.url) {
            return (
                <div className='flex items-center justify-center'>
                    <img src={item.url} alt={item.name} className='max-w-full max-h-32 rounded-lg object-contain' />
                </div>
            )
        }

        // For videos
        if (item.data_type === 'video' && item.url) {
            return <video src={item.url} controls className='w-full max-h-32 rounded-lg' />
        }

        // For HTML content
        if (item.html && item.html.trim()) {
            const sanitizedHtml = DOMPurify.sanitize(item.html, {
                ALLOWED_TAGS: [
                    'p',
                    'strong',
                    'em',
                    'ul',
                    'ol',
                    'li',
                    'h1',
                    'h2',
                    'h3',
                    'h4',
                    'h5',
                    'h6',
                    'a',
                    'br',
                    'blockquote',
                    'code',
                    'pre',
                ],
                ALLOWED_ATTR: ['href', 'class'],
            })
            return (
                <div
                    className='prose-xs prose max-w-none text-xs text-gray-600 line-clamp-6'
                    dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                />
            )
        }

        // For text/markdown content
        if (item.content && typeof item.content === 'string' && item.content.trim()) {
            const truncatedContent = item.content.slice(0, 500)
            return (
                <MarkdownRenderer className='prose-xs prose max-w-none text-xs text-gray-600 line-clamp-6'>
                    {truncatedContent}
                </MarkdownRenderer>
            )
        }

        // No preview available
        return (
            <div className='text-center py-4'>
                <FileText className='w-6 h-6 text-gray-300 mx-auto mb-2' />
                <p className='text-xs text-gray-400'>No preview available</p>
                <button onClick={onOpen} className='text-xs text-[#63316B] hover:underline mt-1'>
                    Open to view
                </button>
            </div>
        )
    }, [item, onOpen, onNavigateToItem])

    return (
        <aside className='flex flex-col h-full overflow-hidden border-l w-72 bg-white border-gray-100 shrink-0 animate-in slide-in-from-right-4 duration-200'>
            <div className='flex items-center justify-between px-4 py-3 border-b border-gray-100'>
                <h3 className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide'>Preview</h3>
                <button onClick={onClose} className='p-1 rounded-lg hover:bg-gray-100 transition-colors'>
                    <X className='w-4 h-4 text-gray-400' />
                </button>
            </div>

            <div className='flex-1 overflow-y-auto p-4'>
                <div className='text-center mb-4'>
                    <div
                        className='w-20 h-20 bg-gray-50 border border-gray-100 rounded-2xl mb-2 flex items-center justify-center mx-auto overflow-hidden cursor-pointer hover:bg-gray-100 transition-colors'
                        onClick={onOpen}
                    >
                        {item.thumbnail || (item.data_type === 'image' && item.url) ? (
                            <img
                                src={item.thumbnail || item.url}
                                alt={item.name}
                                className='w-full h-full object-cover'
                                draggable={false}
                            />
                        ) : (
                            <img src={icon} alt='' className='w-12 h-12' draggable={false} />
                        )}
                    </div>
                    <h2 className='font-semibold text-sm text-gray-900 leading-tight mb-0.5'>{item.name}</h2>
                    <p className='text-[10px] text-gray-400'>
                        {item.type === 'folder' ? 'Folder' : item.extension || 'Document'} • {formatBytes(item.size)}
                    </p>
                </div>

                {/* Content Preview */}
                <div className='mb-4 p-3 bg-gray-50 rounded-xl border border-gray-100'>
                    <h4 className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2'>Content</h4>
                    {contentPreview}
                </div>

                <div className='grid grid-cols-2 gap-1.5 mb-6'>
                    {isTrashSection ? (
                        <>
                            <button
                                onClick={onRestore}
                                className='flex flex-col items-center gap-1.5 p-3 bg-gray-50 rounded-xl hover:bg-green-50 transition-all group col-span-2'
                            >
                                <RotateCcw className='w-4 h-4 text-gray-400 group-hover:text-green-500' />
                                <span className='text-[9px] font-medium text-gray-500 group-hover:text-green-600'>
                                    Restore
                                </span>
                            </button>
                            <button
                                onClick={onPermanentDelete}
                                className='flex flex-col items-center gap-1.5 p-3 bg-gray-50 rounded-xl hover:bg-red-50 transition-all group col-span-2'
                            >
                                <Trash2 className='w-4 h-4 text-gray-400 group-hover:text-red-500' />
                                <span className='text-[9px] font-medium text-gray-500 group-hover:text-red-600'>
                                    Delete Permanently
                                </span>
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={onShare}
                                className='flex flex-col items-center gap-1.5 p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition-all group'
                            >
                                <Share2 className='w-4 h-4 text-gray-400 group-hover:text-blue-500' />
                                <span className='text-[9px] font-medium text-gray-500 group-hover:text-blue-600'>
                                    Share
                                </span>
                            </button>
                            <button
                                onClick={onDownload}
                                className='flex flex-col items-center gap-1.5 p-3 bg-gray-50 rounded-xl hover:bg-green-50 transition-all group'
                            >
                                <ArrowDown className='w-4 h-4 text-gray-400 group-hover:text-green-500' />
                                <span className='text-[9px] font-medium text-gray-500 group-hover:text-green-600'>
                                    Download
                                </span>
                            </button>
                            <button
                                onClick={onFavoriteToggle}
                                className={cn(
                                    'flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all group',
                                    item.favorite ? 'bg-amber-50' : 'bg-gray-50 hover:bg-purple-50'
                                )}
                            >
                                <Star
                                    className={cn(
                                        'w-4 h-4',
                                        item.favorite
                                            ? 'text-amber-500 fill-amber-500'
                                            : 'text-gray-400 group-hover:text-purple-500'
                                    )}
                                />
                                <span
                                    className={cn(
                                        'text-[9px] font-medium',
                                        item.favorite ? 'text-amber-600' : 'text-gray-500 group-hover:text-purple-600'
                                    )}
                                >
                                    {item.favorite ? 'Favorited' : 'Favorite'}
                                </span>
                            </button>
                            <button
                                onClick={onDelete}
                                className='flex flex-col items-center gap-1.5 p-3 bg-gray-50 rounded-xl hover:bg-red-50 transition-all group'
                            >
                                <Trash2 className='w-4 h-4 text-gray-400 group-hover:text-red-500' />
                                <span className='text-[9px] font-medium text-gray-500 group-hover:text-red-600'>
                                    Trash
                                </span>
                            </button>
                        </>
                    )}
                </div>

                <div className='mb-6'>
                    <h4 className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2'>Details</h4>
                    <div className='space-y-1.5'>
                        <div className='flex justify-between items-center bg-gray-50 px-3 py-2 rounded-lg'>
                            <span className='text-[10px] text-gray-400'>Created</span>
                            <span className='text-xs text-gray-700'>{formatDate(item.createdAt)}</span>
                        </div>
                        <div className='flex justify-between items-center bg-gray-50 px-3 py-2 rounded-lg'>
                            <span className='text-[10px] text-gray-400'>Modified</span>
                            <span className='text-xs text-gray-700'>{formatDate(item.updatedAt)}</span>
                        </div>
                        {item.type === 'file' && item.extension && (
                            <div className='flex justify-between items-center bg-gray-50 px-3 py-2 rounded-lg'>
                                <span className='text-[10px] text-gray-400'>Type</span>
                                <span className='text-xs text-gray-700'>{item.extension}</span>
                            </div>
                        )}
                        <div className='flex justify-between items-center bg-gray-50 px-3 py-2 rounded-lg'>
                            <span className='text-[10px] text-gray-400'>Owner</span>
                            <span className='text-xs text-[#63316B]'>{getOwnerName(item)}</span>
                        </div>
                    </div>
                </div>

                {item.tags && item.tags.length > 0 && (
                    <div>
                        <h4 className='text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2'>Tags</h4>
                        <div className='flex flex-wrap gap-1.5'>
                            {item.tags.map(tag => (
                                <span
                                    key={tag}
                                    className='bg-purple-50 text-[#63316B] px-2 py-1 rounded-md text-[10px] border border-purple-100'
                                >
                                    #{tag}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </aside>
    )
}
